/**
 * Echo: the pattern game on the maintenance page. A pad sequence plays, the
 * player repeats it, and every cleared round adds one more pad. These are pure
 * functions so the rules can be tested without timers or a DOM.
 */

export const PAD_COUNT = 4;

export type Pad = 0 | 1 | 2 | 3;
export type Phase = "idle" | "show" | "input" | "over";

export interface EchoState {
  phase: Phase;
  /** The pattern for the current round. */
  sequence: Pad[];
  /** How many pads of the pattern the player has repeated so far. */
  step: number;
  /** Rounds cleared in the current game. */
  score: number;
  /** Most rounds cleared in any game on this device. */
  best: number;
}

export type PressOutcome = "ignored" | "correct" | "round" | "wrong";

export interface PressResult {
  state: EchoState;
  outcome: PressOutcome;
}

export type PadSource = () => Pad;

export function randomPad(): Pad {
  return Math.floor(Math.random() * PAD_COUNT) as Pad;
}

export function initialState(best = 0): EchoState {
  return { phase: "idle", sequence: [], step: 0, score: 0, best };
}

/** A fresh game: a one-pad pattern, played back before the player's turn. */
export function startGame(best: number, nextPad: PadSource = randomPad): EchoState {
  return { phase: "show", sequence: [nextPad()], step: 0, score: 0, best };
}

/** The pattern has finished playing: it is the player's turn. */
export function finishPlayback(state: EchoState): EchoState {
  return state.phase === "show" ? { ...state, phase: "input", step: 0 } : state;
}

export function pressPad(state: EchoState, pad: Pad, nextPad: PadSource = randomPad): PressResult {
  if (state.phase !== "input") return { state, outcome: "ignored" };

  if (state.sequence[state.step] !== pad) {
    return { state: { ...state, phase: "over" }, outcome: "wrong" };
  }

  const step = state.step + 1;
  if (step < state.sequence.length) {
    return { state: { ...state, step }, outcome: "correct" };
  }

  // Round cleared: it counts at once, and the pattern grows by one pad.
  const score = state.sequence.length;
  return {
    state: {
      ...state,
      phase: "show",
      sequence: [...state.sequence, nextPad()],
      step: 0,
      score,
      best: Math.max(state.best, score),
    },
    outcome: "round",
  };
}

/**
 * Milliseconds between pads while the pattern plays. It speeds up with the
 * pattern but never drops below 400 ms, so the flashes stay under three a second.
 */
export function playbackGap(length: number): number {
  return Math.max(400, 640 - length * 20);
}
